import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom"
import Modal from 'react-bootstrap/Modal';
import { collection, deleteDoc, doc, getDocs, onSnapshot, query, setDoc, updateDoc, where, documentId } from "firebase/firestore";
import { db } from "../../Firebase";
import { User } from "../../types/User";
import Spinner from "../../components/spinner/Spinner";
import { useAuth } from "../../contexts/AuthContext";
import { LeaderboardEntry } from "../../types/LeaderboardEntry";
import { dateObjectToDate, timestampToDateObject } from "../../utils/DateConversions";
import Empty from "../../components/Empty";
import { PageTitle } from "../../utils/PageTitle";
import Goback from "../../components/Goback";
import Select from "react-select";
import { hasPermission, roles } from "../../utils/Roles";
import { FaTrashAlt, FaEdit } from 'react-icons/fa'
import { LeaderboardRank } from "../../types/LeaderboardRank";
import { Leaderboard } from "../../types/Leaderboard";

export default function LeaderboardDetail() {
    const { slug } = useParams();

    const [users, setUsers] = useState<User[]>([]);
    const [leaderboard, setLeaderboard] = useState<Leaderboard>();
    const [leaderboardEntries, setLeaderboardEntries] = useState<LeaderboardEntry[]>([]);
    const [leaderboardRankings, setLeaderboardRankings] = useState<LeaderboardRank[]>([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState<null | string>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const userPlayedAgainstRef = useRef<string | null | undefined>(null);
    const currentUserWinRef = useRef<HTMLInputElement>(null);
    const { currentUser, currentUserDoc } = useAuth();
    const navigate = useNavigate();

    // Database calls
    const getUsers = async () => {
        const querySnapshot = await getDocs(query(collection(db, 'users'), where(documentId(), '!=', currentUser!.uid), where('approved', '==', true)));
        const data = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            username: doc.data().username,
            approved: doc.data().approved,
            role: doc.data().role,
            dateJoined: timestampToDateObject(doc.data().dateJoined),
            dateLastLogin: timestampToDateObject(doc.data().dateLastLogin)
        }));

        setUsers(data);
    }

    const getLeaderboardDetails = async () => {
        const leaderboardsRef = collection(db, 'leaderboards');
        const q = query(leaderboardsRef, where('slug', '==', slug));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            navigate('/not-found', { replace: true });
        }
        else {
            const leaderboardDoc: Leaderboard = {
                id: querySnapshot.docs[0].id,
                name: querySnapshot.docs[0].data().name,
                slug: querySnapshot.docs[0].data().slug,
                dateCreated: timestampToDateObject(querySnapshot.docs[0].data().dateCreated),
                dateLastUpdated: timestampToDateObject(querySnapshot.docs[0].data().dateLastUpdated),
            }

            setLeaderboard(leaderboardDoc)

            PageTitle(leaderboardDoc.name);
        }
    }

    const getLeaderboardRanking = async () => {
        const participants = new Set<string>();
        leaderboardEntries.forEach(entry => {
            participants.add(entry.winner);
            participants.add(entry.loser);
        });


        let leaderboardRanks: LeaderboardRank[] = [];
        participants.forEach(participant => leaderboardRanks.push({
            id: participant,
            username: getUsernameById(participant),
            wins: leaderboardEntries.filter(entry => entry.winner === participant).length,
            losses: leaderboardEntries.filter(entry => entry.loser === participant).length,
            gamesPlayed: leaderboardEntries.filter(entry => entry.winner === participant || entry.loser === participant).length,
            winPercentage: 0,
            score: 0,
        }));

        // Skip sorting if leaderboard has less than two entries
        if (leaderboardRanks.length >= 2) {
            // Find player with most wins
            const playerWithMostGames = leaderboardRanks.reduce((prev, current) => {
                return prev.gamesPlayed > current.gamesPlayed ? prev : current;
            });

            leaderboardRanks.forEach((leaderboardRank) => {
                leaderboardRank.winPercentage = (leaderboardRank.wins / leaderboardRank.gamesPlayed) || 0; // Avoid division by zero

                var unroundedScore = leaderboardRank.winPercentage * (leaderboardRank.wins / playerWithMostGames.gamesPlayed)
                leaderboardRank.score = parseFloat(unroundedScore.toFixed(3));
            });

            // Sort the array based on win percentage, games played, and last win date
            leaderboardRanks.sort((a, b) => {
                // Sort by score (descending order)
                if (a.score !== b.score) {
                    return b.score - a.score;
                }

                // Sort by win percentage (descending order)
                if (a.winPercentage !== b.winPercentage) {
                    return b.winPercentage - a.winPercentage;
                }

                // If win percentage is the same, sort by games played (ascending order)
                if (a.gamesPlayed !== b.gamesPlayed) {
                    return a.gamesPlayed - b.gamesPlayed;
                }

                // If games played are the same, tough luck
                return 1;
            });
        }

        setLeaderboardRankings(leaderboardRanks);
        setLoading(false);
    }


    // Execute database calls
    useEffect(() => {
        const executeDatabaseCalls = async () => {
            await getUsers();
            await getLeaderboardDetails();
        }

        executeDatabaseCalls();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // get leaderboard entries via snapshot
    useEffect(() => {
        if (leaderboard === undefined) return

        const q = query(collection(db, 'leaderboardEntries'), where('leaderboardId', '==', leaderboard.id));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            setLoading(true);

            const data: LeaderboardEntry[] = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                leaderboardId: doc.data().leaderboardId,
                winner: doc.data().winner,
                loser: doc.data().loser,
                datePlayed: timestampToDateObject(doc.data().datePlayed)
            }));

            data.sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.datePlayed.getTime() - a.datePlayed.getTime());
            setLeaderboardEntries(data);

            if (data.length === 0) setLoading(false);
        });

        return unsubscribe;
    }, [leaderboard]);


    useEffect(() => {
        if (leaderboard === undefined) return
        if (leaderboardEntries.length === 0) return

        getLeaderboardRanking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaderboard, leaderboardEntries]);


    const updateLastUpdated = async () => {
        await updateDoc(doc(db, 'leaderboards', leaderboard!.id), {
            dateLastUpdated: new Date(),
        });
    }

    const addEntry = async (winnerId: string, loserId: string) => {
        await setDoc(doc(collection(db, "leaderboardEntries")), {
            datePlayed: new Date(),
            leaderboardId: leaderboard!.id,
            winner: winnerId,
            loser: loserId,
        });

        updateLastUpdated();
    }

    const deleteEntry = async () => {
        if (showDeleteModal != null) {
            await deleteDoc(doc(db, "leaderboardEntries", showDeleteModal));

            setShowDeleteModal(null);
            updateLastUpdated();
        }
    }

    const getUsernameById = (id: string) => {
        if (id === currentUser?.uid) return 'Me';
        if (users.find(user => id === user.id) == null) return 'Disabled user';

        return users.find(user => id === user.id)!.username!;
    }

    // modals
    const AddModal = () => {
        return (<>
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Add entry</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form>
                        <Form.Group className="form-group">
                            <Select
                                options={users.map(user => ({ value: user.id, label: user.username }))}
                                onChange={(element) => userPlayedAgainstRef.current = element?.value}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </Form.Group>
                        <Form.Group className="form-group">
                            <Form.Check
                                type="switch"
                                id="win"
                                label="Win?"
                                ref={currentUserWinRef}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={() => handleAddModalSubmit()}>Save</Button>
                </Modal.Footer>
            </Modal>
        </>
        )
    }

    const handleAddModalSubmit = () => {
        let passed = true;
        setError('');

        if (userPlayedAgainstRef.current === null || userPlayedAgainstRef.current === undefined) {
            passed = false;
            setError("Select a player");
        }

        if (passed) {
            if (currentUserWinRef.current!.checked) {
                addEntry(currentUser!.uid, userPlayedAgainstRef.current!)
            } else {
                addEntry(userPlayedAgainstRef.current!, currentUser!.uid);
            }

            userPlayedAgainstRef.current = null;
            setShowAddModal(false);
        }
    }

    const EditModal = () => {
        return (<>
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Edit entry</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Not yet implemented
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={() => console.log("Not yet implemented")}>Edit</Button>
                </Modal.Footer>
            </Modal>
        </>
        )
    }

    const DeleteModal = () => {
        return (<>
            <Modal show={showDeleteModal != null} onHide={() => setShowDeleteModal(null)} centered >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Delete entry</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Are you sure you want to delete this entry?
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="danger" onClick={() => deleteEntry()}>Delete</Button>
                </Modal.Footer>
            </Modal>
        </>
        )
    }

    if (loading) return <Spinner />;

    return (
        <>
            <div className="mb-4 d-flex justify-content-between">
                <Goback />
                <Button onClick={() => setShowAddModal(true)}>Add</Button>
            </div>

            {leaderboardEntries.length === 0
                ? <Empty />
                : <>
                    <Table className="mb-5" striped bordered>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Wins</th>
                                <th>Losses</th>
                                <th>Total</th>
                                <th>Win %</th>
                                <th>Score</th>
                            </tr>
                        </thead>

                        <tbody>
                            {leaderboardRankings.map((ranking, i) =>
                                <tr key={'rank-' + ranking.id} className={'rank-' + (i + 1)}>
                                    <td>{i + 1}</td>
                                    <td>{ranking.username}</td>
                                    <td>{ranking.wins}</td>
                                    <td>{ranking.losses}</td>
                                    <td>{ranking.gamesPlayed}</td>
                                    <td>{Math.round(ranking.winPercentage * 100)}%</td>
                                    <td>{ranking.score}</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>


                    <h1>Entries</h1>
                    <Table striped bordered>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Date</th>
                                <th>Winner</th>
                                <th>Loser</th>
                                {hasPermission(roles.EDITOR.permissionLevel, currentUserDoc!.role) &&
                                    <th></th>
                                }
                            </tr>
                        </thead>

                        <tbody>
                            {leaderboardEntries.map((entry, i) =>
                                <tr key={'entry-' + entry.id}>
                                    <td>{leaderboardEntries.length - i}</td>
                                    <td>{dateObjectToDate(entry.datePlayed, 'YYYY/MM/DD, HH:mm')}</td>
                                    <td>{getUsernameById(entry.winner)}</td>
                                    <td>{getUsernameById(entry.loser)}</td>
                                    {hasPermission(roles.EDITOR.permissionLevel, currentUserDoc!.role) &&
                                        <td>
                                            <FaEdit className="clickableIcon" onClick={() => setShowEditModal(true)} />
                                            <FaTrashAlt className="clickableIcon" onClick={() => setShowDeleteModal(entry.id)} />
                                        </td>
                                    }
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </>
            }

            <AddModal />
            <EditModal />
            <DeleteModal />
        </>
    )
}
